import { allFields, formatNumber, humanize } from './schema';
import { compareValues, sortKey } from './table';
import { same } from './values';
import type { DataClassRecord, DataRecord, OwnedField, RefValue, Values } from './types';

// The sheet view (kalkylark) of a category: one row per entry of a list (a quest's steps, a dialogue's
// pages), with the record's own fields repeated on every row, so it sorts and filters like a spreadsheet.
// Only the presentation changes: an edit is the same values save as in the form. Our own columns
// (difficulty, notes) are stored apart in data_sheet and never reach Unity. Pure helpers (sheet.test.ts).

export type DerivedId = 'objectiveTypes' | 'factions' | 'choices';

export type SheetCell =
  /** The record's name: its name field when the category has one, else the asset name (read-only). */
  | { kind: 'name' }
  /** A top-level value of the record. */
  | { kind: 'field'; field: string }
  /** A value of the row's list entry (the quest step, the dialogue page). */
  | { kind: 'entry'; field: string }
  /** The entry's number, from 1. */
  | { kind: 'index' }
  /** A read-only summary of nested values. */
  | { kind: 'derived'; id: DerivedId }
  /** A column of our own, per record (data_sheet). `options` are stored values; labels are in i18n. */
  | { kind: 'own'; name: string; options?: string[]; multiline?: boolean }
  | { kind: 'status' }
  | { kind: 'changedBy' };

export interface SheetColumnSpec {
  id: string;
  cell: SheetCell;
  /** Default width in px. */
  width?: number;
  /** Long text: filtered by "contains" instead of a list of values. */
  long?: boolean;
  /** i18n key shown (muted) when the cell is empty, e.g. "N/A". */
  empty?: string;
}

export interface SheetSpec {
  /** The list that gives one row per entry. A record whose list is empty still gets one row. */
  list: string;
  columns: SheetColumnSpec[];
}

/** Categories with a sheet view. Add one here to get it; the view itself is generic. */
export const SHEETS: Record<string, SheetSpec> = {
  quests: {
    list: 'steps',
    columns: [
      { id: 'name', cell: { kind: 'name' }, width: 210 },
      { id: 'step', cell: { kind: 'index' }, width: 72 },
      { id: 'stepName', cell: { kind: 'entry', field: 'stepName' }, width: 170 },
      { id: 'journalRegion', cell: { kind: 'field', field: 'journalRegion' }, width: 130 },
      { id: 'questGiverNPC', cell: { kind: 'field', field: 'questGiverNPC' }, width: 140 },
      { id: 'isRepeatable', cell: { kind: 'field', field: 'isRepeatable' }, width: 128 },
      { id: 'objectiveTypes', cell: { kind: 'derived', id: 'objectiveTypes' }, width: 130 },
      { id: 'description', cell: { kind: 'field', field: 'description' }, width: 300, long: true },
      { id: 'stepDescription', cell: { kind: 'entry', field: 'stepDescription' }, width: 260, long: true },
      { id: 'locationText', cell: { kind: 'field', field: 'locationText' }, width: 150 },
      { id: 'turnInDialogue', cell: { kind: 'field', field: 'turnInDialogue' }, width: 300, long: true },
      { id: 'factions', cell: { kind: 'derived', id: 'factions' }, width: 150, empty: 'data.sheet.na' },
      {
        id: 'difficulty',
        cell: { kind: 'own', name: 'difficulty', options: ['easy', 'medium', 'hard'] },
        width: 100,
      },
      { id: 'notes', cell: { kind: 'own', name: 'notes', multiline: true }, width: 260, long: true },
      { id: 'status', cell: { kind: 'status' }, width: 120 },
      { id: 'changedBy', cell: { kind: 'changedBy' }, width: 120 },
    ],
  },
  dialogue: {
    list: 'pages',
    columns: [
      { id: 'name', cell: { kind: 'name' }, width: 240 },
      { id: 'page', cell: { kind: 'index' }, width: 72 },
      { id: 'npcName', cell: { kind: 'field', field: 'npcName' }, width: 120 },
      { id: 'bodyText', cell: { kind: 'entry', field: 'bodyText' }, width: 420, long: true },
      { id: 'animationCue', cell: { kind: 'entry', field: 'animationCue' }, width: 110 },
      { id: 'choices', cell: { kind: 'derived', id: 'choices' }, width: 260, long: true },
      { id: 'status', cell: { kind: 'status' }, width: 120 },
      { id: 'changedBy', cell: { kind: 'changedBy' }, width: 120 },
    ],
  },
};

export interface SheetColumn {
  id: string;
  cell: SheetCell;
  width: number;
  long: boolean;
  empty?: string;
  /** The schema of the value for name, field and entry cells. */
  field?: OwnedField;
}

/** The entry class of a list field (QuestData.steps → QuestStepData), if its entries are nested classes. */
function entryClass(field: OwnedField | undefined): string | undefined {
  return field?.kind === 'list' && field.element?.kind === 'struct' ? field.element.type : undefined;
}

/**
 * The spec's columns that exist in this schema: a field renamed or removed in C# drops out until the
 * spec is updated, rather than showing an empty column.
 */
export function sheetColumns(
  spec: SheetSpec,
  classes: Record<string, DataClassRecord>,
  className: string,
  nameField: string,
): SheetColumn[] {
  const fields = allFields(classes, className);
  const entryType = entryClass(fields.find((f) => f.name === spec.list));
  const entryFields = entryType ? allFields(classes, entryType) : [];
  const out: SheetColumn[] = [];
  for (const c of spec.columns) {
    let field: OwnedField | undefined;
    if (c.cell.kind === 'field') {
      const name = c.cell.field;
      field = fields.find((f) => f.name === name);
      if (!field) continue;
    } else if (c.cell.kind === 'entry') {
      const name = c.cell.field;
      field = entryFields.find((f) => f.name === name);
      if (!field) continue;
    } else if (c.cell.kind === 'name' && nameField) {
      field = fields.find((f) => f.name === nameField);
    }
    out.push({ id: c.id, cell: c.cell, width: c.width ?? 140, long: !!c.long, empty: c.empty, field });
  }
  return out;
}

export interface SheetRow {
  /** `${recordId}:${index}` */
  id: string;
  record: DataRecord;
  /** Position in the record's list; -1 for the one row of a record whose list is empty. */
  index: number;
}

export function sheetRows(records: DataRecord[], list: string): SheetRow[] {
  return records.flatMap((record) => {
    const entries = record.values?.[list];
    const n = Array.isArray(entries) ? entries.length : 0;
    if (n === 0) return [{ id: `${record.id}:-1`, record, index: -1 }];
    return Array.from({ length: n }, (_, index) => ({ id: `${record.id}:${index}`, record, index }));
  });
}

export interface SheetCtx {
  list: string;
  /** The category's name field ('' = records are named after their asset). */
  nameField: string;
  refName: (ref: RefValue) => string | undefined;
  userName: (id: string) => string;
  /** Our own columns of a record (data_sheet). */
  own: (recordId: string) => Record<string, string>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function entryAt(values: Values | null | undefined, list: string, index: number): Values | undefined {
  const entries = values?.[list];
  if (!Array.isArray(entries) || index < 0) return undefined;
  const e: unknown = entries[index];
  return e && typeof e === 'object' ? (e as Values) : undefined;
}

/** Where a cell's value lives in the record's values; null for cells that aren't record values. */
function valuePath(cell: SheetCell, ctx: SheetCtx): { field: string; entry: boolean } | null {
  switch (cell.kind) {
    case 'name':
      return ctx.nameField ? { field: ctx.nameField, entry: false } : null;
    case 'field':
      return { field: cell.field, entry: false };
    case 'entry':
      return { field: cell.field, entry: true };
    default:
      return null;
  }
}

/** Whether the cell shows a value of the record (and so can differ from Unity and be edited). */
export function holdsValue(row: SheetRow, cell: SheetCell, ctx: SheetCtx): boolean {
  const p = valuePath(cell, ctx);
  return !!p && (!p.entry || row.index >= 0);
}

/** Whether the cell belongs to the row's entry (step, page) rather than to the whole record. */
export function perEntry(cell: SheetCell): boolean {
  return (
    cell.kind === 'entry' ||
    cell.kind === 'index' ||
    (cell.kind === 'derived' && (cell.id === 'objectiveTypes' || cell.id === 'choices'))
  );
}

/** The design value of a cell that holds a record value. */
export function cellValue(row: SheetRow, cell: SheetCell, ctx: SheetCtx): unknown {
  const p = valuePath(cell, ctx);
  if (!p) return undefined;
  const from = p.entry ? entryAt(row.record.values, ctx.list, row.index) : row.record.values;
  return from?.[p.field];
}

export interface CellChange {
  /** Unity's value (undefined when Unity doesn't have this entry). */
  unity: unknown;
  /** false: the entry exists on the web only (a step or page added here), so there is nothing to revert to. */
  inUnity: boolean;
}

/** How a value cell differs from Unity, or null when it doesn't (or the record isn't in Unity yet). */
export function cellChange(row: SheetRow, cell: SheetCell, ctx: SheetCtx): CellChange | null {
  const p = valuePath(cell, ctx);
  if (!p || !row.record.guid || (p.entry && row.index < 0)) return null;
  const unityFrom = p.entry ? entryAt(row.record.unityValues, ctx.list, row.index) : row.record.unityValues;
  if (!unityFrom) return { unity: undefined, inUnity: false };
  const unity = unityFrom[p.field];
  return same(cellValue(row, cell, ctx), unity) ? null : { unity, inUnity: true };
}

/** The record's values with one value cell set to `v` (unchanged for other cells, or a missing entry). */
export function withCellValue(
  values: Values,
  row: SheetRow,
  cell: SheetCell,
  ctx: SheetCtx,
  v: unknown,
): Values {
  const p = valuePath(cell, ctx);
  if (!p) return values;
  if (!p.entry) return { ...values, [p.field]: v };
  const entries = values[ctx.list];
  if (!Array.isArray(entries) || row.index < 0 || row.index >= entries.length) return values;
  return {
    ...values,
    [ctx.list]: entries.map((e: unknown, i) => (i === row.index ? { ...(e as Values), [p.field]: v } : e)),
  };
}

/** A stored value as plain text: enums humanized, bools yes/no, refs by name. */
export function valueText(v: unknown, field: OwnedField | undefined, ctx: SheetCtx): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? ctx.t('data.yes') : ctx.t('data.no');
  if (typeof v === 'number') return formatNumber(v);
  if (typeof v === 'string') return field?.kind === 'enum' ? humanize(v) : v;
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' ? humanize(x) : String(x))).join(', ');
  if (typeof v === 'object') return ctx.refName(v as RefValue) ?? '';
  return String(v);
}

function unique(xs: string[]): string[] {
  return [...new Set(xs.filter((x) => x !== ''))];
}

/** The parts of a derived cell (one per objective type, faction, choice). */
function derivedParts(row: SheetRow, id: DerivedId, ctx: SheetCtx): string[] {
  const list = (v: unknown): Values[] =>
    Array.isArray(v) ? v.filter((x): x is Values => !!x && typeof x === 'object') : [];
  switch (id) {
    case 'objectiveTypes':
      return unique(
        list(entryAt(row.record.values, ctx.list, row.index)?.objectives).map((o) =>
          typeof o.objectiveType === 'string' ? humanize(o.objectiveType) : '',
        ),
      );
    case 'factions':
      // The faction a quest moves: its Reputation rewards.
      return unique(
        list(row.record.values?.rewards)
          .filter((r) => r.rewardType === 'Reputation' && r.rewardFaction)
          .map((r) => ctx.refName(r.rewardFaction as RefValue) ?? '?'),
      );
    case 'choices':
      return list(entryAt(row.record.values, ctx.list, row.index)?.choices).map((c) => {
        const label = typeof c.label === 'string' ? c.label : '';
        const scene = c.playSequence as RefValue | null | undefined;
        const target =
          scene && (scene.guid || scene.new)
            ? `▶ ${ctx.refName(scene) ?? ctx.t('data.sheet.scene')}`
            : typeof c.targetPageIndex === 'number' && c.targetPageIndex >= 0
              ? ctx.t('data.sheet.toPage', { n: c.targetPageIndex + 1 })
              : ctx.t('data.sheet.ends');
        return `${label} → ${target}`;
      });
  }
}

function ownText(row: SheetRow, cell: Extract<SheetCell, { kind: 'own' }>, ctx: SheetCtx): string {
  const v = ctx.own(row.record.id)[cell.name] ?? '';
  return v && cell.options ? ctx.t(`data.sheet.options.${cell.name}.${v}`, { defaultValue: v }) : v;
}

export function statusText(r: DataRecord, ctx: SheetCtx): string {
  return r.syncState === 'missing_in_unity' ? ctx.t('sync.missing_in_unity') : ctx.t(`status.${r.status}`);
}

/** The cell as plain text: what is shown, searched, filtered on and copied. */
export function cellText(row: SheetRow, col: SheetColumn, ctx: SheetCtx): string {
  const cell = col.cell;
  switch (cell.kind) {
    case 'name':
      return ctx.nameField ? valueText(cellValue(row, cell, ctx), col.field, ctx) : row.record.name;
    case 'field':
    case 'entry':
      return valueText(cellValue(row, cell, ctx), col.field, ctx);
    case 'index':
      return row.index >= 0 ? String(row.index + 1) : '';
    case 'derived':
      return derivedParts(row, cell.id, ctx).join(cell.id === 'choices' ? '\n' : ', ');
    case 'own':
      return ownText(row, cell, ctx);
    case 'status':
      return statusText(row.record, ctx);
    case 'changedBy':
      return row.record.changedBy ? ctx.userName(row.record.changedBy) : '';
  }
}

/** The values a column's filter list offers for this cell ('' = empty): derived cells split into parts. */
export function cellValues(row: SheetRow, col: SheetColumn, ctx: SheetCtx): string[] {
  if (col.cell.kind === 'derived' && col.cell.id !== 'choices') {
    const parts = derivedParts(row, col.cell.id, ctx);
    return parts.length ? parts : [''];
  }
  return [cellText(row, col, ctx)];
}

/** What a column sorts by: numbers as numbers, our option columns in option order, the rest as text. */
export function sortValue(row: SheetRow, col: SheetColumn, ctx: SheetCtx): unknown {
  const cell = col.cell;
  if (cell.kind === 'index') return row.index >= 0 ? row.index : null;
  if (cell.kind === 'own' && cell.options) {
    const i = cell.options.indexOf(ctx.own(row.record.id)[cell.name] ?? '');
    return i >= 0 ? i : null;
  }
  if (cell.kind === 'field' || cell.kind === 'entry') {
    const v = cellValue(row, cell, ctx);
    if (typeof v === 'number') return v;
  }
  return cellText(row, col, ctx);
}

// ── filters, sorting, search ──────────────────────────────────────────────────────────────

/** A column filter: the values to keep (from the list), or text the cell must contain. */
export type ColumnFilter = { values: string[] } | { text: string };

export interface SheetSort {
  /** Column id; null = by record name, then entry. */
  col: string | null;
  dir: 1 | -1;
}

export interface SheetView {
  sort: SheetSort;
  filters: Record<string, ColumnFilter>;
  search: string;
  onlyChanged: boolean;
}

export const DEFAULT_VIEW: SheetView = {
  sort: { col: null, dir: 1 },
  filters: {},
  search: '',
  onlyChanged: false,
};

export function isFilterActive(f: ColumnFilter | undefined): boolean {
  return !!f && ('text' in f ? f.text.trim() !== '' : true);
}

export function passesFilter(row: SheetRow, col: SheetColumn, f: ColumnFilter, ctx: SheetCtx): boolean {
  if ('text' in f) {
    const needle = f.text.trim().toLowerCase();
    return !needle || cellText(row, col, ctx).toLowerCase().includes(needle);
  }
  return cellValues(row, col, ctx).some((v) => f.values.includes(v));
}

/** Whether any value cell of the row differs from Unity, or the record is new on the web. */
export function rowChanged(row: SheetRow, cols: SheetColumn[], ctx: SheetCtx): boolean {
  if (!row.record.guid) return true;
  return cols.some((c) => cellChange(row, c.cell, ctx) !== null);
}

/**
 * The rows that pass the view's search, "only changed" and column filters (`skip` leaves one column's
 * filter out, for counting that column's choices). `cols` are all columns, also hidden ones.
 */
export function filterRows(
  rows: SheetRow[],
  cols: SheetColumn[],
  view: SheetView,
  ctx: SheetCtx,
  skip?: string,
): SheetRow[] {
  const active = cols.filter((c) => c.id !== skip && isFilterActive(view.filters[c.id]));
  const needle = view.search.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (!view.onlyChanged || rowChanged(row, cols, ctx)) &&
      active.every((c) => passesFilter(row, c, view.filters[c.id], ctx)) &&
      (!needle ||
        row.record.key.toLowerCase().includes(needle) ||
        cols.some((c) => cellText(row, c, ctx).toLowerCase().includes(needle))),
  );
}

export interface ValueOption {
  value: string;
  count: number;
}

/** The choices of a column's filter list over `rows` (already narrowed by the other columns), A–Ö, empty last. */
export function valueOptions(rows: SheetRow[], col: SheetColumn, ctx: SheetCtx): ValueOption[] {
  const counts = new Map<string, number>();
  for (const row of rows)
    for (const v of new Set(cellValues(row, col, ctx))) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts]
    .map(([value, count]) => ({ value, count }))
    .sort(
      (a, b) =>
        Number(a.value === '') - Number(b.value === '') ||
        a.value.localeCompare(b.value, 'sv', { numeric: true }),
    );
}

function byRecord(a: SheetRow, b: SheetRow): number {
  return (
    a.record.name.localeCompare(b.record.name, 'sv', { numeric: true }) ||
    a.record.id.localeCompare(b.record.id) ||
    a.index - b.index
  );
}

/** Sorted rows; ties (and the default) keep a record's entries together and in order. Missing sorts last. */
export function sortRows(
  rows: SheetRow[],
  col: SheetColumn | undefined,
  dir: 1 | -1,
  ctx: SheetCtx,
): SheetRow[] {
  if (!col) return [...rows].sort((a, b) => byRecord(a, b) * (a.record.id === b.record.id ? 1 : dir));
  const keyed = rows.map((row) => ({ row, v: sortValue(row, col, ctx) }));
  keyed.sort((a, b) => {
    const c = compareValues(a.v, b.v);
    if ((sortKey(a.v) === null) !== (sortKey(b.v) === null)) return c;
    return c * dir || byRecord(a.row, b.row);
  });
  return keyed.map((k) => k.row);
}

// ── clipboard ─────────────────────────────────────────────────────────────────────────────

/** Cells as tab-separated text, the way Excel and Google Sheets paste it (quotes around tabs/newlines). */
export function toTsv(grid: string[][]): string {
  const quote = (s: string) => (/[\t\n\r"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  return grid.map((r) => r.map(quote).join('\t')).join('\n');
}

/** Tab-separated text (as copied from a spreadsheet) back into cells. */
export function parseTsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === '\t') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

export type EditorKind = 'text' | 'multiline' | 'number' | 'bool' | 'enum' | 'own';

/**
 * Text typed or pasted into a cell as the value to store, or undefined when it doesn't fit the column.
 * `choices` are the enum names (or our option values) with their labels.
 */
export function parseInput(
  text: string,
  kind: EditorKind,
  opts: { int?: boolean; choices?: { value: string; label: string }[] } = {},
): unknown {
  const s = text.trim();
  switch (kind) {
    case 'text':
      return text.replace(/\r?\n/g, ' ');
    case 'multiline':
      return text.replace(/\r\n/g, '\n');
    case 'number': {
      if (s === '') return undefined;
      const n = Number(s.replace(',', '.'));
      if (!isFinite(n) || (opts.int && !Number.isInteger(n))) return undefined;
      return n;
    }
    case 'bool': {
      const l = s.toLowerCase();
      if (['true', 'ja', 'yes', '1', 'x', '✓'].includes(l)) return true;
      if (['false', 'nej', 'no', '0', ''].includes(l)) return false;
      return undefined;
    }
    case 'enum':
    case 'own': {
      if (kind === 'own' && s === '') return '';
      const l = s.toLowerCase();
      return (opts.choices ?? []).find((c) => c.value.toLowerCase() === l || c.label.toLowerCase() === l)
        ?.value;
    }
  }
}
